/**
 * Coalescing helpers used to tame the release-time WebSocket-event fan-out that
 * 503'd the release center (#1801).
 *
 * A single release NOTIFY delivers a burst of `messageReceipt/created` /
 * `messageReceipt/updated` events to every connected client, and multiple hooks
 * used to react to each event with its own un-throttled request. With N online
 * clients that turned one release into ~2N simultaneous expensive queries and
 * one delivery-flag write per receipt id, backing the connection-acquire queue
 * past the 5s timeout. These primitives collapse such a burst into a single
 * trailing action.
 */

/**
 * A trailing debounce that also de-duplicates across multiple callers.
 *
 * Any number of components may `register` a runnable; a burst of `schedule()`
 * calls (from any of them, from many WS events) collapses into ONE invocation
 * of a single registered runnable after `delayMs` of quiet. This is what lets
 * the two mounts of the pending-count hook (nav badge + release-center notice)
 * share one refetch instead of each firing its own on every event: they read
 * the same shared store bank, so a single refetch updates both.
 *
 * `jitterMs` spreads the arrival ACROSS clients (#2059). Coalescing fixes the
 * per-client fan-out but every client starts its timer off the same broadcast WS
 * event, so a fixed delay makes all N of them fire in lockstep at +delayMs — the
 * request rate is the same spike, just moved. Each scheduled run instead lands
 * uniformly in [delayMs, delayMs + jitterMs). Default 0 keeps the old behaviour
 * for callers that do not opt in.
 */
export function createCoalescedRunner(delayMs: number, jitterMs = 0) {
  const runners = new Set<() => void>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const clear = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };

  return {
    /** Register a runnable; returns an unregister fn (call on unmount). */
    register(run: () => void): () => void {
      runners.add(run);
      return () => {
        runners.delete(run);
        if (runners.size === 0) {
          clear();
        }
      };
    },
    /** Request a trailing run; repeated calls within the window coalesce. */
    schedule(): void {
      if (runners.size === 0) {
        return;
      }
      clear();
      timer = setTimeout(
        () => {
          timer = undefined;
          // One registered runner is enough: all registrants act on the same
          // shared state, so a single run satisfies every mount.
          for (const run of runners) {
            run();
            break;
          }
        },
        delayMs + Math.random() * jitterMs,
      );
    },
    /** Number of registered runnables (test/introspection only). */
    get size(): number {
      return runners.size;
    },
    /** Whether a trailing run is currently pending (test/introspection only). */
    get pending(): boolean {
      return timer !== undefined;
    },
  };
}

/**
 * Micro-batches keys that arrive close together into a single async flush.
 *
 * Each `add(key)` returns a promise that resolves with the flush result for the
 * whole batch; keys arriving within `delayMs` share one `flush(keys)` call.
 * Duplicate keys in a window are de-duplicated. Used by the release center to
 * turn a burst of per-receipt `messageReceipt/updated` refetches into one
 * `id$in` request instead of one GET per id.
 *
 * `jitterMs` spreads the flush across clients for the same reason as
 * `createCoalescedRunner` above (#2059): the batch window opens on a broadcast
 * WS event, so without it every client flushes at the same instant. Rolled once
 * per batch, when the window opens. Default 0 preserves the old behaviour.
 */
export function createBatcher<K, R>(
  flush: (keys: K[]) => Promise<R>,
  delayMs: number,
  jitterMs = 0,
) {
  let pending = new Set<K>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let waiters: Array<{ resolve: (r: R) => void; reject: (e: unknown) => void }> = [];

  const run = () => {
    const keys = [...pending];
    const settle = waiters;
    pending = new Set();
    waiters = [];
    timer = undefined;

    flush(keys).then(
      (result) => settle.forEach((w) => w.resolve(result)),
      (error) => settle.forEach((w) => w.reject(error)),
    );
  };

  return {
    add(key: K): Promise<R> {
      pending.add(key);
      const promise = new Promise<R>((resolve, reject) => {
        waiters.push({ resolve, reject });
      });
      if (timer === undefined) {
        timer = setTimeout(run, delayMs + Math.random() * jitterMs);
      }
      return promise;
    },
  };
}
